/**
 * Data Catalog — frontend types + remote loader.
 *
 * The actual catalog is served by the backend at `GET /api/data-catalog`
 * (see `app/api/routes/data_catalog.py`). The backend reads the same
 * Mongo collections the agent already uses (`function_docs`,
 * `screener_docs`, `fmp_schemas`) so we can never drift out of sync.
 *
 * The frontend caches the response in module-level state so the popover
 * opens instantly after the first fetch, and uses an AbortController to
 * dedupe in-flight fetches if the user opens / closes the popover quickly.
 */

import { API_BASE_URL } from "@/lib/config";

export type DataKind = "function" | "template" | "screener" | "fmp" | "alerts";

export type DataSource = {
  id: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  tool_names: string[];
  kind: DataKind;
  fmp_endpoint?: string;
  alerts_scope?: "user" | "public";
};

export type DataCatalog = {
  sources: DataSource[];
  categories: string[];
  tags: string[];
  version: number;
  fetched_at: number;
};

export type RequestedDataSource = {
  id: string;
  name: string;
  sources: string[];
  fmp_endpoint?: string;
  alerts_scope?: "user" | "public";
};

// ── Tag styling ──────────────────────────────────────────────────────────
//
// One source of truth for every tag color. The dot legend in the footer,
// the chip filter row, and the per-row tag pills all read from here.

export type TagStyle = { dot: string; chip: string; chipActive: string; pill: string };

const FALLBACK_STYLE: TagStyle = {
  dot: "bg-zinc-400",
  chip: "bg-zinc-900 text-zinc-200 ring-1 ring-zinc-700 hover:bg-zinc-800",
  chipActive: "bg-zinc-200 text-zinc-950 ring-1 ring-zinc-200",
  pill: "bg-zinc-800 text-zinc-200 ring-1 ring-zinc-700",
};

export const TAG_STYLES: Record<string, TagStyle> = {
  Backtestable: {
    dot: "bg-emerald-400",
    chip: "bg-emerald-400/10 text-emerald-300 ring-1 ring-emerald-400/30 hover:bg-emerald-400/20",
    chipActive: "bg-emerald-400 text-zinc-950 ring-1 ring-emerald-400",
    pill: "bg-emerald-400/15 text-emerald-300 ring-1 ring-emerald-400/30",
  },
  Realtime: {
    dot: "bg-sky-400",
    chip: "bg-sky-400/10 text-sky-300 ring-1 ring-sky-400/30 hover:bg-sky-400/20",
    chipActive: "bg-sky-400 text-zinc-950 ring-1 ring-sky-400",
    pill: "bg-sky-400/15 text-sky-300 ring-1 ring-sky-400/30",
  },
  Screener: {
    dot: "bg-fuchsia-400",
    chip: "bg-fuchsia-400/10 text-fuchsia-300 ring-1 ring-fuchsia-400/30 hover:bg-fuchsia-400/20",
    chipActive: "bg-fuchsia-400 text-zinc-950 ring-1 ring-fuchsia-400",
    pill: "bg-fuchsia-400/15 text-fuchsia-300 ring-1 ring-fuchsia-400/30",
  },
  Template: {
    dot: "bg-violet-400",
    chip: "bg-violet-400/10 text-violet-300 ring-1 ring-violet-400/30 hover:bg-violet-400/20",
    chipActive: "bg-violet-400 text-zinc-950 ring-1 ring-violet-400",
    pill: "bg-violet-400/15 text-violet-300 ring-1 ring-violet-400/30",
  },
  FMP: {
    dot: "bg-amber-400",
    chip: "bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/30 hover:bg-amber-400/20",
    chipActive: "bg-amber-400 text-zinc-950 ring-1 ring-amber-400",
    pill: "bg-amber-400/15 text-amber-300 ring-1 ring-amber-400/30",
  },
  Options: {
    dot: "bg-orange-400",
    chip: "bg-orange-400/10 text-orange-300 ring-1 ring-orange-400/30 hover:bg-orange-400/20",
    chipActive: "bg-orange-400 text-zinc-950 ring-1 ring-orange-400",
    pill: "bg-orange-400/15 text-orange-300 ring-1 ring-orange-400/30",
  },
  Macro: {
    dot: "bg-teal-400",
    chip: "bg-teal-400/10 text-teal-300 ring-1 ring-teal-400/30 hover:bg-teal-400/20",
    chipActive: "bg-teal-400 text-zinc-950 ring-1 ring-teal-400",
    pill: "bg-teal-400/15 text-teal-300 ring-1 ring-teal-400/30",
  },
  Futures: {
    dot: "bg-rose-400",
    chip: "bg-rose-400/10 text-rose-300 ring-1 ring-rose-400/30 hover:bg-rose-400/20",
    chipActive: "bg-rose-400 text-zinc-950 ring-1 ring-rose-400",
    pill: "bg-rose-400/15 text-rose-300 ring-1 ring-rose-400/30",
  },
  Index: {
    dot: "bg-blue-400",
    chip: "bg-blue-400/10 text-blue-300 ring-1 ring-blue-400/30 hover:bg-blue-400/20",
    chipActive: "bg-blue-400 text-zinc-950 ring-1 ring-blue-400",
    pill: "bg-blue-400/15 text-blue-300 ring-1 ring-blue-400/30",
  },
  Crypto: {
    dot: "bg-yellow-400",
    chip: "bg-yellow-400/10 text-yellow-300 ring-1 ring-yellow-400/30 hover:bg-yellow-400/20",
    chipActive: "bg-yellow-400 text-zinc-950 ring-1 ring-yellow-400",
    pill: "bg-yellow-400/15 text-yellow-300 ring-1 ring-yellow-400/30",
  },
  Forex: {
    dot: "bg-indigo-400",
    chip: "bg-indigo-400/10 text-indigo-300 ring-1 ring-indigo-400/30 hover:bg-indigo-400/20",
    chipActive: "bg-indigo-400 text-zinc-950 ring-1 ring-indigo-400",
    pill: "bg-indigo-400/15 text-indigo-300 ring-1 ring-indigo-400/30",
  },
  Search: {
    dot: "bg-cyan-400",
    chip: "bg-cyan-400/10 text-cyan-300 ring-1 ring-cyan-400/30 hover:bg-cyan-400/20",
    chipActive: "bg-cyan-400 text-zinc-950 ring-1 ring-cyan-400",
    pill: "bg-cyan-400/15 text-cyan-300 ring-1 ring-cyan-400/30",
  },
};

export function tagStyle(tag: string): TagStyle {
  return TAG_STYLES[tag] ?? FALLBACK_STYLE;
}

// ── Category icons ───────────────────────────────────────────────────────
//
// One icon per CATEGORY HEADER (rendered next to the section title).
// Rows don't have icons anymore — they were noisy and unhelpful.

export const CATEGORY_ICON: Record<string, string> = {
  "Alerts": "Bell",
  "Backtesting": "ChartLineUp",
  "Screeners": "Funnel",
  "Options": "TreeStructure",
  "Stocks": "TrendUp",
  "Fundamentals": "Calculator",
};

// ── Fuzzy scoring ────────────────────────────────────────────────────────

export function scoreDataSource(source: DataSource, q: string): number {
  if (!q) return 1;
  const query = q.trim().toLowerCase();
  if (!query) return 1;

  const name = source.name.toLowerCase();
  if (name === query) return 1;
  if (name.startsWith(query)) return 0.95;
  if (name.includes(query)) return 0.85;

  const cat = source.category.toLowerCase();
  if (cat.includes(query)) return 0.75;

  for (const tool of source.tool_names) {
    const t = tool.toLowerCase();
    if (t === query) return 0.88;
    if (t.startsWith(query)) return 0.78;
    if (t.includes(query)) return 0.68;
  }

  for (const tag of source.tags) {
    if (tag.toLowerCase().includes(query)) return 0.6;
  }

  const desc = source.description.toLowerCase();
  if (desc.includes(query)) return 0.5;

  return 0;
}

// ── Remote loader ────────────────────────────────────────────────────────

let _CACHE: DataCatalog | null = null;
let _IN_FLIGHT: Promise<DataCatalog> | null = null;

/** Load the catalog from the backend. Result is cached for the lifetime
 *  of the page. Concurrent callers share a single in-flight request. */
export async function loadDataCatalog(): Promise<DataCatalog> {
  if (_CACHE) return _CACHE;
  if (_IN_FLIGHT) return _IN_FLIGHT;

  const url = `${API_BASE_URL}/api/data-catalog`;
  _IN_FLIGHT = fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  })
    .then(async (res) => {
      if (!res.ok) {
        throw new Error(`data-catalog HTTP ${res.status}`);
      }
      const json = (await res.json()) as DataCatalog;
      _CACHE = json;
      return json;
    })
    .catch((err) => {
      _IN_FLIGHT = null;
      // Re-throw so callers can render a clear error state.
      throw err;
    });

  return _IN_FLIGHT;
}

/** Force a refresh next time `loadDataCatalog` is called. */
export function invalidateDataCatalog(): void {
  _CACHE = null;
  _IN_FLIGHT = null;
}
